import { z } from "zod";

export const ROLES = ["operator", "admin", "analyst", "staff"] as const;
export type Role = (typeof ROLES)[number];

/**
 * The authenticated user. Derived exclusively from a verified GoTab JWT.
 * `locationIds` holds the user's scope ids: the hard upper bound on what data this user can ever
 * see. It keeps its old name because principals are stored in session cookies and MCP grants;
 * the stage 5 auth adapter renames it.
 */
export const PrincipalSchema = z.object({
  userId: z.string().min(1),
  orgId: z.string().min(1),
  name: z.string().optional(),
  email: z.string().optional(),
  locationIds: z.array(z.string()),
  roles: z.array(z.enum(ROLES)).min(1),
});
export type Principal = z.infer<typeof PrincipalSchema>;

export const isStaff = (p: Principal) => p.roles.includes("staff");

/**
 * Claim contract for the GoTab-issued SSO token. Documented in docs/sso.md so
 * the GoTab side can implement the issuer.
 */
export const GoTabClaimsSchema = z.object({
  sub: z.string(),
  org_id: z.string(),
  location_ids: z.array(z.union([z.string(), z.number()]).transform(String)),
  roles: z.array(z.enum(ROLES)).min(1),
  name: z.string().optional(),
  email: z.string().optional(),
});
export type GoTabClaims = z.infer<typeof GoTabClaimsSchema>;

export function principalFromClaims(c: GoTabClaims): Principal {
  return {
    userId: c.sub,
    orgId: c.org_id,
    name: c.name,
    email: c.email,
    locationIds: c.location_ids,
    roles: c.roles,
  };
}
